import React, { useState } from "react";
import {
  Card,
  CardImage,
  CardBadge,
  CardBody,
  CardPrice,
  CardTitle,
  CardSubtitle,
  CardSpecs,
} from "../styles/MFRealStateStyles";
import {
  formatPrice,
  buildSpecsStructured,
  buildPropertySlug,
  optimizedImageUrl,
} from "../services/propertyService";
import { MF_BASE } from "../basePath";

const SPEC_ICONS = {
  area: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 12h18" />
      <path d="M12 3v18" />
    </svg>
  ),
  quartos: (
    <svg viewBox="0 0 24 24">
      <path d="M3 21V7l9-4 9 4v14" />
      <path d="M9 21V13h6v8" />
      <path d="M3 10h18" />
    </svg>
  ),
  banheiros: (
    <svg viewBox="0 0 24 24">
      <path d="M4 12h16a1 1 0 011 1v3a3 3 0 01-3 3H6a3 3 0 01-3-3v-3a1 1 0 011-1z" />
      <path d="M6 12V7a2 2 0 012-2h1a2 2 0 012 2v5" />
    </svg>
  ),
  vagas: (
    <svg viewBox="0 0 24 24">
      <rect x="1" y="3" width="22" height="18" rx="2" />
      <path d="M12 7v4" />
      <path d="M7 7v4" />
      <path d="M17 7v4" />
    </svg>
  ),
};

const tabBtn = (active) => ({
  flex: 1,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.5,
  border: "none",
  background: active ? "var(--mf-accent-warm)" : "transparent",
  color: active ? "#FFF" : "var(--mf-text-secondary)",
  cursor: "pointer",
  borderRadius: 4,
  transition: "all 0.15s",
});

const PLACEHOLDER_IMG =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400">
    <rect fill="#E7E9EC" width="600" height="400"/>
    <text x="50%" y="50%" font-family="sans-serif" font-size="18" fill="#A0A6B2" text-anchor="middle" dy=".3em">Sem imagem</text>
  </svg>`,
  );

const VIDEO_EXTS = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
const isVideo = (url) => VIDEO_EXTS.test(url || "");

export default function PropertyCard({ property, defaultTab }) {
  const specs = buildSpecsStructured(property);
  const firstImage = property.imagens?.find((url) => !isVideo(url));
  const firstVideo = property.imagens?.find((url) => isVideo(url));
  const cover = firstImage || firstVideo || null;
  const coverIsVideo = !firstImage && !!firstVideo;

  const getBadgeText = () => {
    if (property.destaque) return "Destaque";
    if (property.tipo === "studio") return "Studio";
    return null;
  };

  const badge = getBadgeText();

  const hasVenda = Number(property.preco) > 0;
  const hasAluguel = Number(property.preco_aluguel) > 0;
  const initialTab =
    defaultTab === "aluguel" && hasAluguel
      ? "aluguel"
      : defaultTab === "venda" && hasVenda
        ? "venda"
        : hasVenda
          ? "venda"
          : "aluguel";
  const [tab, setTab] = useState(initialTab);

  React.useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const stop = (e) => {
    e.stopPropagation();
  };

  const priceVenda = hasVenda ? formatPrice(property.preco) : null;
  const priceAluguel = hasAluguel
    ? `${formatPrice(property.preco_aluguel)}/mes`
    : null;
  const currentPrice = tab === "aluguel" ? priceAluguel : priceVenda;

  const title =
    property.condominio || property.titulo || property.endereco || "";
  const subtitle = [property.bairro, property.cidade]
    .filter(Boolean)
    .join(", ");

  return (
    <Card
      onClick={() => {
        window.location.href = `${MF_BASE}/imoveis/${buildPropertySlug(property)}`;
      }}
    >
      <CardImage>
        {coverIsVideo ? (
          <>
            <video
              src={`${cover}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              tabIndex={-1}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "rgba(15, 24, 40, 0.72)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ marginLeft: 3 }}
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </>
        ) : (
          <img
            src={optimizedImageUrl(cover, { width: 800, quality: 80 }) || PLACEHOLDER_IMG}
            alt={property.titulo}
            onError={(e) => {
              e.target.src = PLACEHOLDER_IMG;
            }}
            loading="lazy"
          />
        )}
        {badge && <CardBadge>{badge}</CardBadge>}
      </CardImage>

      <CardBody>
        <div
          onClick={stop}
          style={{
            display: "flex",
            gap: 4,
            background: "var(--mf-accent-warm-soft)",
            padding: 1,
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          {hasVenda && hasAluguel ? (
            <>
              <button
                type="button"
                style={tabBtn(tab === "venda")}
                onClick={(e) => {
                  stop(e);
                  setTab("venda");
                }}
              >
                VENDA
              </button>
              <button
                type="button"
                style={tabBtn(tab === "aluguel")}
                onClick={(e) => {
                  stop(e);
                  setTab("aluguel");
                }}
              >
                ALUGUEL
              </button>
            </>
          ) : (
            <button type="button" style={tabBtn(true)}>
              {hasAluguel ? "ALUGUEL" : "VENDA"}
            </button>
          )}
        </div>
        {currentPrice && <CardPrice>{currentPrice}</CardPrice>}
        <CardTitle>{title}</CardTitle>
        {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        <CardSpecs>
          {specs.map((spec, i) => (
            <span key={i}>
              {SPEC_ICONS[spec.type]}
              <strong>{spec.value}</strong>
              {spec.unit && <small>{spec.unit}</small>}
            </span>
          ))}
        </CardSpecs>
      </CardBody>
    </Card>
  );
}
